import { useEffect, useMemo } from 'react';

import { AutoCompleteSelectUI, FormLabelUI } from '../Interface';

const normalizeCompanyDocReports = (documentTypes, currentReports = [], companyId = null) => {
  const activeDocumentTypes = (documentTypes ?? []).filter((docType) => docType.active);

  return activeDocumentTypes.map((docType) => {
    const existing = currentReports.find((report) => Number(report.documentTypeId) === Number(docType.documentTypeId));

    return {
      id: existing?.id,
      companyId: existing?.companyId ?? companyId,
      documentTypeId: docType.documentTypeId,
      reportId: existing?.reportId ?? null,
      createdBy: existing?.createdBy,
      updatedBy: existing?.updatedBy,
      deletedBy: existing?.deletedBy,
    };
  });
};

const areReportsEqual = (left = [], right = []) => {
  if (left.length !== right.length) return false;

  return left.every((item, index) => {
    const other = right[index];

    return (
      item?.id === other?.id &&
      item?.companyId === other?.companyId &&
      item?.documentTypeId === other?.documentTypeId &&
      item?.reportId === other?.reportId &&
      item?.createdBy === other?.createdBy &&
      item?.updatedBy === other?.updatedBy &&
      item?.deletedBy === other?.deletedBy
    );
  });
};

const getReportOptions = (docType) => {
  return (docType?.printReport ?? []).map((report) => ({
    value: report.id,
    label: [report.name, report.language?.code].filter(Boolean).join(' - '),
  }));
};

const CompanyDocReportsTab = ({ disabled, documentTypes = [], selectedValues, valuesState }) => {
  const { values, setFieldValue } = valuesState;

  useEffect(() => {
    if ((documentTypes ?? []).length === 0) return;

    const normalized = normalizeCompanyDocReports(documentTypes, values.companyDocReports ?? [], selectedValues?.id ?? null);

    if (!areReportsEqual(values.companyDocReports ?? [], normalized)) {
      setFieldValue('companyDocReports', normalized, false);
    }
  }, [documentTypes, values.companyDocReports, selectedValues?.id, setFieldValue]);

  const rows = useMemo(() => {
    const normalizedReports = normalizeCompanyDocReports(
      documentTypes,
      values.companyDocReports ?? [],
      selectedValues?.id ?? null
    );

    return (documentTypes ?? [])
      .filter((docType) => docType.active)
      .map((docType) => ({
        documentTypeId: docType.documentTypeId,
        documentTypeName: docType.name,
        reportOptions: getReportOptions(docType),
        selectedReport: normalizedReports.find((report) => Number(report.documentTypeId) === Number(docType.documentTypeId)),
      }));
  }, [documentTypes, values.companyDocReports, selectedValues?.id]);

  return (
    <div className="space-y-4 p-2">
      {rows.map((row) => (
        <div key={row.documentTypeId} className="grid grid-cols-1 gap-3 md:grid-cols-2 items-center">
          <div className="font-medium">{row.documentTypeName}</div>
          <AutoCompleteSelectUI
            id={`companyDocReports-${row.documentTypeId}`}
            fullWidth
            label={<FormLabelUI text="Riport" />}
            dataset={row.reportOptions}
            disabled={disabled}
            selectedValue={row.selectedReport?.reportId ?? null}
            selectedLabelValue={
              row.reportOptions.find((option) => option.value === row.selectedReport?.reportId)?.label ?? ''
            }
            onChange={(_event, newVal) => {
              const nextReports = normalizeCompanyDocReports(
                documentTypes,
                values.companyDocReports ?? [],
                selectedValues?.id ?? null
              ).map((report) =>
                Number(report.documentTypeId) === Number(row.documentTypeId)
                  ? {
                      ...report,
                      reportId: newVal?.value ?? null,
                    }
                  : report
              );

              setFieldValue('companyDocReports', nextReports);
            }}
          />
        </div>
      ))}
    </div>
  );
};

export default CompanyDocReportsTab;
